import * as React from "react"

/**
 * Devolve uma callback ref para a lista rolável de um Popover portalado de
 * dentro de um Dialog/Sheet modal.
 *
 * O `react-remove-scroll` do Radix instala um listener de `wheel`/`touchmove`
 * no `document` (fase de bubbling) que dá `preventDefault` e bloqueia a rolagem
 * da lista — que vive fora do diálogo por causa do portal. Interrompemos a
 * propagação desses eventos no nível da lista (antes de chegarem ao
 * `document`), para que a rolagem nativa do overflow volte a funcionar.
 *
 * O listener precisa ser nativo (não-React) para vencer o do `document`, e é
 * anexado por uma callback ref — e não por um `useEffect([open])` — porque o
 * conteúdo do Popover é portalado um tick depois de `open` virar true, então no
 * efeito o ref ainda estaria `null` e o listener nunca seria anexado. Como
 * fallback (caso o listener do Radix rode em captura, antes de nós), aplicamos
 * a rolagem manualmente quando o evento já vier cancelado.
 */
export function usePortalledListScroll() {
  const cleanupRef = React.useRef<(() => void) | null>(null)

  return React.useCallback((node: HTMLElement | null) => {
    cleanupRef.current?.()
    cleanupRef.current = null
    if (!node) return
    const onWheel = (e: WheelEvent) => {
      e.stopPropagation()
      if (e.defaultPrevented) {
        const factor =
          e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? node.clientHeight : 1
        node.scrollTop += e.deltaY * factor
      }
    }
    const onTouchMove = (e: TouchEvent) => e.stopPropagation()
    node.addEventListener("wheel", onWheel, { passive: true })
    node.addEventListener("touchmove", onTouchMove, { passive: true })
    cleanupRef.current = () => {
      node.removeEventListener("wheel", onWheel)
      node.removeEventListener("touchmove", onTouchMove)
    }
  }, [])
}
